import type { ErrorOr } from "@glide/error-info";
import { isErrorInfo } from "@glide/error-info";
import type { ChangeObservable } from "@glide/support";
import { logError, RecurrentBackgroundJob, Watchable } from "@glide/support";

export const numPersistRetries = 10;

interface MutationEntry<TMutation> {
    readonly mutation: TMutation;
    // TODO: We will have to add the `version` later when we support applying
    // mutations on the backend.
}

export interface PersistResult<TState> {
    readonly newState: TState;
    readonly newVersion: number;
    readonly errorMessage: string | undefined;
}

interface ApplyCallbacks<TState, TMutation> {
    /** The state won't be updated if this returns true. */
    areStatesEqual: (a: TState, b: TState) => boolean;
    /** `undefined` means that the mutation was a no-op. */
    applyMutation(
        state: TState,
        mutation: TMutation
    ): ErrorOr<{ newState: TState; conditionalMutation: TMutation; antiMutation: TMutation | undefined }> | undefined;
}

interface PersistCallbacks<TState, TMutation> {
    /**
     * If there was an error persisting the mutations at all, such a a network
     * issue, this should return an `ErrorInfo`.  Otherwise, it must return a
     * new state and version, and optionally an error message if any of the
     * individual mutations failed to apply.
     *
     * This will never be called concurrently.
     */
    persistMutations(mutations: readonly TMutation[]): Promise<ErrorOr<PersistResult<TState>>>;
    /**
     * This is called after saving succeeded or failed.  If `errorMessage` is
     * undefined, it means that the save succeeded.
     */
    traceSave(durationMS: number, numTries: number, numMutations: number, errorMessage: string | undefined): void;
    /**
     * This is called if there was an error persisting the mutations at all,
     * after a number of retries.  We make this return a promise so that in
     * the future we can ask the user to make a decision.
     */
    informUserOfSaveFailure(errorMessage: string): Promise<void>;
    /** This is just `sleep`, but we inject it for testing. */
    sleep(ms: number): Promise<void>;
    /** This must return a function that calls `f` after the debouncing time
     * to use for persisting. */
    debouncePersist(f: () => void): () => void;
    /** This might be called even if the number of unpersisted mutations
     * hasn't changed, but it will definitely be called if it has changed. */
    unpersistedMutationsChanged(numMutations: number): void;
}

/**
 * This keeps track of locally applied optimistic mutations, and persists them
 * via a callback.  It assumes that the state on the backend has a strictly
 * monotonically increasing version number.  It can deal with updates from the
 * backend arriving at any time, in any order, because it disregards updates
 * that are older than the version it already has.
 */
export class OptimisticMutationManager<TState, TMutation> {
    private readonly watchable: Watchable<TState>;
    /** This ensures that we send mutations serially */
    private readonly job = new RecurrentBackgroundJob(() => this.callBackend());
    /** These are ordered */
    private mutations: readonly MutationEntry<TMutation>[] = [];
    /** This is safe to call from within the job, and will cause it to run
     * again, after the debounce. */
    private readonly requestDebounced: (() => void) | undefined;
    /** If true, we will not call the backend. */
    private isRetired = false;
    private gotStateFromBackend: boolean;

    constructor(
        private backendState: TState,
        private backendVersion: number,
        isFromBackend: boolean,
        private readonly applyCallbacks: ApplyCallbacks<TState, TMutation>,
        /** If no persist callbacks are provided, we will not persist, i.e.
         * we'll be read-only. */
        private readonly persistCallbacks: PersistCallbacks<TState, TMutation> | undefined
    ) {
        this.requestDebounced = this.persistCallbacks?.debouncePersist(() => this.job.request());
        this.watchable = new Watchable(backendState);
        this.gotStateFromBackend = isFromBackend;
    }

    public get changeObservable(): ChangeObservable<TState> {
        return this.watchable;
    }

    public get currentVersion(): number {
        return this.backendVersion;
    }

    /** Returns whether we've ever got state from the backend. Note that this
     * doesn't mean that the current state is equal to the backend state! */
    public get hasStateFromBackend(): boolean {
        return this.gotStateFromBackend;
    }

    public updateFromBackend(state: TState, version: number): TState | undefined {
        // If this older than the version we have, we ignore it.
        if (version <= this.backendVersion) return undefined;

        this.backendState = state;
        this.backendVersion = version;
        this.gotStateFromBackend = true;
        this.updateLocally();
        return this.watchable.current;
    }

    private setNewState(newState: TState): void {
        // If the value is still the same we currently have, we avoid
        // notifying listeners.
        if (!this.applyCallbacks.areStatesEqual(this.watchable.current, newState)) {
            this.watchable.current = newState;
        }
    }

    public addMutation(mutation: TMutation): ErrorOr<[newState: TState, antiMutation: TMutation | undefined]> {
        const result = this.applyCallbacks.applyMutation(this.watchable.current, mutation);
        if (result === undefined) return [this.watchable.current, undefined];
        if (isErrorInfo(result)) return result;

        const { newState, conditionalMutation, antiMutation } = result;

        this.mutations = [...this.mutations, { mutation: conditionalMutation }];

        this.setNewState(newState);

        this.requestDebounced?.();
        this.persistCallbacks?.unpersistedMutationsChanged(this.mutations.length);

        return [this.watchable.current, antiMutation];
    }

    public forcePersist(): Promise<void> {
        this.job.request();
        return this.job.wait();
    }

    private updateLocally(): void {
        let state = this.backendState;
        for (const entry of this.mutations) {
            const { mutation } = entry;
            const result = this.applyCallbacks.applyMutation(state, mutation);
            if (result === undefined || isErrorInfo(result)) {
                // The mutation doesn't apply right now, but it might later on
                // because a deletion is undone by another user for example.
                continue;
            }
            state = result.newState;
        }
        this.setNewState(state);
    }

    private async callBackend(): Promise<void> {
        const { persistCallbacks } = this;
        if (persistCallbacks === undefined) return;

        // We know here that we're the only ones processing these mutations,
        // courtesy of the `RecurrentBackgroundJob`.

        const startTime = performance.now();

        let numRetries = 0;
        for (;;) {
            if (this.isRetired) return;

            const mutations = this.mutations;
            if (mutations.length === 0) return;

            const result = await persistCallbacks.persistMutations(mutations.map(m => m.mutation));

            const durationMS = performance.now() - startTime;

            if (isErrorInfo(result)) {
                logError(`Error persisting mutations, retrying: ${result.message}`);
                numRetries++;
                if (numRetries <= numPersistRetries) {
                    await persistCallbacks.sleep(1000);
                    continue;
                }

                persistCallbacks.traceSave(durationMS, numRetries, mutations.length, result.message);
                await persistCallbacks.informUserOfSaveFailure(result.message);

                // New mutations might have been added in the meantime
                this.mutations = this.mutations.filter(m => !mutations.includes(m));
            } else {
                persistCallbacks.traceSave(durationMS, numRetries, mutations.length, undefined);

                // New mutations might have been added in the meantime
                this.mutations = this.mutations.filter(m => !mutations.includes(m));

                if (result.newVersion > this.backendVersion) {
                    this.backendVersion = result.newVersion;
                    this.backendState = result.newState;
                }
            }

            // At this point we've either succeeded in persisting, or failed
            // to persist after retrying.  We need to `updateLocally` because
            // we might have changed `this.mutations` and/or
            // `this.backendState`.

            this.updateLocally();

            persistCallbacks.unpersistedMutationsChanged(this.mutations.length);

            if (this.mutations.length > 0) {
                // New mutations have arrived in the meantime, but we need to
                // observe the debounce, so we request a re-run via the
                // debounce.
                this.requestDebounced?.();
            }
            break;
        }
    }

    public retire(): void {
        this.isRetired = true;
    }
}
